/**
 * sessionStore — estado de sessão do cliente (Zustand).
 * Consolidado a partir de `GET /app/me` + cookies + marca. Ver types/session.ts.
 *
 * REGRA (§11): Zustand só para estado global de CLIENTE. Dados de servidor ficam no React Query.
 */
import { create } from 'zustand';
import type { MeResponse, Session } from '../types/session';
import { getBrand } from '../config/brand';
import { readAuth } from '../lib/auth';
import { TAG_LIMIT_DEFAULT } from '../config/constants';
import { cachedLanguage } from '../lib/panelLanguage';

interface SessionState {
  session: Session | null;
  setFromMe: (me: MeResponse) => void;
  clear: () => void;
}

function buildSession(me: MeResponse): Session {
  const brand = getBrand();
  const auth = readAuth();
  return {
    source: brand.source,
    userId: auth?.userId ?? me.id ?? '',
    email: auth?.email ?? me.email ?? '',
    status: me.status ?? 'active',
    expiredAt: me.expiredAt,
    demo: me.demo ?? false,
    modules: me.modules ?? {},
    survey: me.survey ?? false,
    aware: me.aware,
    profile: me.profile ?? undefined,
    classic: me.classic,
    dualMode: me.dualMode !== false,
    // Ambassador: mescla os campos do /me (slug/points/exchangeRate/stats/history).
    ambassador: me.ambassador
      ? {
          slug: me.ambassador.slug,
          // Backend [app.js:731]: ambassador.points = stats.points_confirmed.
          points: me.ambassador.points ?? me.ambassador.stats?.points_confirmed,
          genarate: me.ambassador.genarate,
          exchangeRate: me.ambassador.exchangeRate,
          discount: me.ambassador.discount,
          access: me.ambassador.stats?.access ?? me.ambassador.access,
          purchases: me.ambassador.stats?.purchases ?? me.ambassador.purchases,
          points_confirmed: me.ambassador.stats?.points_confirmed ?? me.ambassador.points_confirmed,
          points_pending: me.ambassador.stats?.points_pending ?? me.ambassador.points_pending,
          history: me.ambassador.history,
        }
      : undefined,
    tagLimit: me.tagLimit ?? TAG_LIMIT_DEFAULT,
    entitlements: me.entitlements,
    // Conta > Preferências: a escolha deste navegador vem antes do idioma da marca.
    language: cachedLanguage() ?? me.language ?? brand.defaultLang,
  };
}

export const useSessionStore = create<SessionState>((set) => ({
  session: null,
  setFromMe: (me) => set({ session: buildSession(me) }),
  clear: () => set({ session: null }),
}));

